import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { X } from "lucide-react";
import { Type } from "@shared/ui/Type";
import { Button } from "@shared/ui/Button";
import { MdxRenderer } from "@shared/ui/MdxRenderer";
import { Notification } from "../../notifications.types";
import { formatNotificationDate } from "../../utils/formatNotificationDate";

export type Props = {
  notification: Notification;
  onMarkAsRead?: (id: number) => void;
  isRefetching: boolean;
  isExpanded: boolean;
  isAnyExpanded: boolean;
  onExpand: (notificationId: number) => void;
  onCollapse: (notificationId: number) => void;
};

export function NotificationItem({
  notification,
  onMarkAsRead,
  isRefetching,
  isExpanded,
  isAnyExpanded,
  onExpand,
  onCollapse,
}: Props) {
  const [isDisabled, setIsDisabled] = useState(false);

  const isUnread = !notification.read_at;

  const handleClick = () => {
    if (isExpanded) {
      onCollapse(notification.id);
    } else {
      onExpand(notification.id);

      if (isUnread) {
        onMarkAsRead?.(notification.id);
      }
    }
  };

  const handleActionUrlClick = () => {
    if (notification.action_url) {
      window.open(notification.action_url, "_blank");
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();

    setIsDisabled(true);

    onMarkAsRead?.(notification.id);
  };

  useEffect(() => {
    if (!isRefetching) {
      setIsDisabled(false);
    }
  }, [isRefetching]);

  return (
    <div
      className={twMerge(
        "flex flex-col cursor-pointer border-b first:rounded-t-md last:border-b-0 last:rounded-b-md border-brand-700 transition-all duration-100  hover:border-brand-600 hover:shadow-md hover:border-t first:hover:border-t-0",
        isExpanded
          ? "border-t shadow-md first:border-t-0"
          : "opacity-40 hover:opacity-100",
        !isAnyExpanded && "opacity-100"
      )}
    >
      <div
        className={twMerge(
          "flex flex-col gap-4 p-4 items-start h-full overflow-x-hidden justify-between md:flex-row md:gap-16",
          isExpanded && "flex-col-reverse md:p-8"
        )}
        onClick={handleClick}
      >
        <div className="flex flex-col flex-1 gap-2 md:pr-4 overflow-hidden">
          <div className="flex flex-row flex-1 items-start gap-2">
            {isUnread && (
              <span
                className={twMerge(
                  "mt-1.5 w-2 h-2 rounded-full shrink-0",
                  isUnread && !isExpanded ? "bg-brand-300" : "bg-transparent"
                )}
              />
            )}
            <div className="flex flex-col gap-0.5 flex-1 w-full">
              <Type
                variant={isExpanded ? "h4" : isUnread ? "h5" : "caption"}
                className={twMerge(
                  !isUnread && "font-semibold",
                  isExpanded && "max-w-full truncate-none",
                  !isExpanded && "truncate max-w-xs md:max-w-xl"
                )}
              >
                {notification.title}
              </Type>
              {!isExpanded && (
                <Type
                  variant="caption"
                  className={twMerge(
                    "text-brand-700 truncate",
                    !isExpanded && "truncate max-w-xs md:max-w-xl"
                  )}
                >
                  {notification.body}
                </Type>
              )}
            </div>
          </div>
        </div>
        <div className="w-full md:w-auto">
          <div className="flex flex-row gap-2 justify-between items-center">
            <Type
              variant="caption"
              className="text-brand-700 text-right truncate"
            >
              {formatNotificationDate(notification.created_at)}
            </Type>
            {isExpanded && (
              <Button
                variant="ghost"
                className="ml-2"
                iconComponent={X}
                onClick={() => onCollapse(notification.id)}
              />
            )}
            {isUnread && !isExpanded && (
              <Button
                variant="ghost"
                className="ml-2"
                onClick={handleMarkAsRead}
                disabled={isDisabled}
              >
                Mark as read
              </Button>
            )}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div
          className="flex flex-col gap-8 px-4 pb-4 md:pb-8 md:px-8 md:gap-12"
          onClick={() => onCollapse(notification.id)}
        >
          <MdxRenderer content={notification.body || ""} />

          {notification.action_url && (
            <Button
              variant="primary"
              className="w-fit"
              onClick={handleActionUrlClick}
            >
              Take Action
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationItem;
